import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const api = axios.create({
  baseURL: `${API_URL}/api`,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('accessToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor to handle token refresh
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 403 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        const refreshToken = localStorage.getItem('refreshToken');
        const response = await axios.post(`${API_URL}/api/auth/refresh`, {
          refreshToken
        });

        const { accessToken } = response.data;
        localStorage.setItem('accessToken', accessToken);

        originalRequest.headers.Authorization = `Bearer ${accessToken}`;
        return api(originalRequest);
      } catch (refreshError) {
        // Refresh failed, logout user
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        window.location.href = '/login';
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  register: (data) => api.post('/auth/register', data),
  login: (data) => api.post('/auth/login', data),
  getProfile: () => api.get('/auth/me'),
  refresh: (refreshToken) => api.post('/auth/refresh', { refreshToken })
};

// Users API
export const usersAPI = {
  updateProfile: (data) => api.patch('/users/profile', data),
  getChildren: () => api.get('/users/children'),
  addChild: (data) => api.post('/users/children', data),
  updateChild: (childId, data) => api.patch(`/users/children/${childId}`, data),
  deleteChild: (childId) => api.delete(`/users/children/${childId}`),
  getChildPickupPoints: (childId) => api.get(`/users/children/${childId}/pickup-points`),
  createChildPickupPoint: (childId, data) => api.post(`/users/children/${childId}/pickup-points`, data),
  updateChildPickupPoint: (childId, pickupPointId, data) =>
    api.patch(`/users/children/${childId}/pickup-points/${pickupPointId}`, data),
  deleteChildPickupPoint: (childId, pickupPointId) =>
    api.delete(`/users/children/${childId}/pickup-points/${pickupPointId}`)
};

// Carpools API
export const carpoolsAPI = {
  getCarpools: () => api.get('/carpools'),
  discoverCarpools: (params) => api.get('/carpools/discover', { params }),
  getCarpool: (id) => api.get(`/carpools/${id}`),
  createCarpool: (data) => api.post('/carpools', data),
  joinCarpool: (id) => api.post(`/carpools/${id}/join`),
  updateCarpool: (id, data) => api.patch(`/carpools/${id}`, data),
  deleteCarpool: (id) => api.delete(`/carpools/${id}`),
  leaveCarpool: (id) => api.post(`/carpools/${id}/leave`),
  removeMember: (carpoolId, userId) => api.delete(`/carpools/${carpoolId}/members/${userId}`),
  getInvitations: (carpoolId) => api.get(`/carpools/${carpoolId}/invitations`),
  getUsageStats: () => api.get('/carpools/usage'),
  updateAvailability: (id, availability) =>
    api.patch(`/carpools/${id}/availability`, { availability }),
  getPickupPoints: (carpoolId) => api.get(`/carpools/${carpoolId}/pickup-points`),
  createPickupPoint: (carpoolId, data) => api.post(`/carpools/${carpoolId}/pickup-points`, data),
  updatePickupPoint: (carpoolId, pickupPointId, data) =>
    api.patch(`/carpools/${carpoolId}/pickup-points/${pickupPointId}`, data),
  deletePickupPoint: (carpoolId, pickupPointId) =>
    api.delete(`/carpools/${carpoolId}/pickup-points/${pickupPointId}`),
};

// Invitations API
export const invitationsAPI = {
  sendInvitation: (data) => api.post('/invitations/send', data),
  getInvitations: () => api.get('/invitations'),
  acceptInvitation: (id) => api.post(`/invitations/${id}/accept`),
  declineInvitation: (id) => api.post(`/invitations/${id}/decline`),
  sendReminder: (id) => api.post(`/invitations/${id}/remind`),
  cancelInvitation: (id) => api.delete(`/invitations/${id}`),
  createInviteLink: (carpoolId) => api.post('/invitations/create-link', { carpoolId }),
  validateInvitation: (token) => {
    // Public endpoint, no auth required
    return axios.get(`${API_URL}/api/invitations/validate/${token}`);
  }
};

// Schedule API
export const scheduleAPI = {
  generateSchedule: (data) => api.post('/schedule/generate', data),
  getSchedule: (carpoolId) => api.get(`/schedule/${carpoolId}`),
  createRide: (data) => api.post('/schedule/rides', data),
  updateRide: (rideId, data) => api.patch(`/schedule/rides/${rideId}`, data),
  deleteRide: (rideId) => api.delete(`/schedule/rides/${rideId}`),
  deleteSchedule: (carpoolId) => api.delete(`/schedule/${carpoolId}`),
  getMyRides: () => api.get('/schedule/my/driving'),
  requestDriver: (rideId) => api.post(`/schedule/rides/${rideId}/request-driver`),
  approveDriverRequest: (rideId, requestId) =>
    api.post(`/schedule/rides/${rideId}/requests/${requestId}/approve`),
  declineDriverRequest: (rideId, requestId) =>
    api.post(`/schedule/rides/${rideId}/requests/${requestId}/decline`),
  unassignDriver: (rideId) => api.post(`/schedule/rides/${rideId}/unassign-driver`)
};

// Tracking API
export const trackingAPI = {
  saveLocation: (data) => api.post('/tracking/location', data),
  getLocationHistory: (userId, params) => 
    api.get(`/tracking/history/${userId}`, { params }),
  getCurrentLocation: (userId) => api.get(`/tracking/current/${userId}`)
};

// Admin API
export const adminAPI = {
  getStats: () => api.get('/admin/stats'),
  getUsers: (params) => api.get('/admin/users', { params }),
  getUser: (userId) => api.get(`/admin/users/${userId}`),
  updateUser: (userId, data) => api.patch(`/admin/users/${userId}`, data),
  deleteUser: (userId) => api.delete(`/admin/users/${userId}`),
  getCarpools: (params) => api.get('/admin/carpools', { params }),
  getRides: (params) => api.get('/admin/rides', { params })
};

export default api;

